import { createCSVRow, generateCSVHeaders } from '../utils/csvContentUtils';

/**
 * Interface for position tracking data
 */
interface CompetitorPosition {
  current?: number | null;
  previous?: number | null;
}

interface PositionTrackingItem {
  keyword: string;
  volume?: number | string;
  cpc?: number | string;
  yourPosition?: number | string;
  previousPosition?: number | string;
  movement?: number | string;
  competitors?: Record<string, CompetitorPosition>;
  [key: string]: unknown; // For competitor positions with dynamic keys
}

/**
 * Competitor letters to match what's shown in the UI table
 */
const COMPETITOR_LETTERS = ['A', 'B', 'C', 'D', 'E'];

/**
 * Display competitor domain names that match what's shown in the UI
 */
const UI_COMPETITORS = [
  "competitor-a.com",
  "competitor-b.com",
  "competitor-c.com",
  "competitor-d.com",
  "competitor-e.com"
];

/**
 * Internal competitor names from mock data
 */
const INTERNAL_COMPETITORS = [
  "competitor1.com",
  "competitor2.com",
  "competitor3.com",
  "competitor4.com",
  "competitor5.com"
];

/**
 * Generate CSV content specifically for position tracking data
 */
export const generatePositionTrackingCSV = (data: {data: Array<PositionTrackingItem>}): string => {
  if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
    console.log("No data to generate CSV");
    return '';
  }
  
  // HACK: Force deep clone the data to avoid any reference issues
  const clonedData = JSON.parse(JSON.stringify(data.data));
  
  // Debugging: Log the first item to see its structure
  const firstItem = clonedData[0];
  console.log("FIXED CSV GENERATOR - First item keyword:", firstItem.keyword);
  console.log("FIXED CSV GENERATOR - yourPosition:", firstItem.yourPosition);
  console.log("FIXED CSV GENERATOR - previousPosition:", firstItem.previousPosition);
  
  // CRITICAL DEBUG: Check competitor data
  if (!firstItem.competitors || Object.keys(firstItem.competitors).length === 0) {
    console.error("CRITICAL ERROR: Missing competitors object in data!");
  } else {
    console.log("FIXED CSV GENERATOR - Competitors found:", Object.keys(firstItem.competitors).join(", "));
    
    // Check a specific competitor
    if (firstItem.competitors["competitor1.com"]) {
      const comp = firstItem.competitors["competitor1.com"];
      console.log("FIXED CSV GENERATOR - competitor1.com data:", 
        JSON.stringify(comp, null, 2));
    }
  }
  
  // Create base headers for position tracking
  const headers = ["Keyword", "Volume", "CPC", "Your Position", "Previous Position", "Movement"];
  
  // Add competitor domains to headers with Position, Previous Position, and Movement columns
  // Include the competitor letter to match the UI
  UI_COMPETITORS.forEach((domain, index) => {
    const letter = COMPETITOR_LETTERS[index];
    headers.push(`${letter}: ${domain} Position`);
    headers.push(`${letter}: ${domain} Previous Position`);
    headers.push(`${letter}: ${domain} Movement`);
  });
  
  // Create CSV content with headers
  let csvContent = generateCSVHeaders(headers);
  
  // Add data rows
  clonedData.forEach((item) => {
    // Start with base data
    const values = [
      item.keyword || '',
      item.volume || '',
      typeof item.cpc === 'number' ? item.cpc.toFixed(2) : (item.cpc || '0.00'),
      item.yourPosition || 'Not ranking',
      item.previousPosition || 'Not ranking',
      item.movement || '0'
    ];
    
    // CRITICAL: For each item, find and log the specific key we're looking for
    console.log(`--------- Processing ${item.keyword} ---------`);
    console.log(`${item.keyword} - competitors:`, item.competitors ? Object.keys(item.competitors) : "No competitors");
    
    // Process competitors in order
    for (let i = 0; i < INTERNAL_COMPETITORS.length; i++) {
      const internalName = INTERNAL_COMPETITORS[i];
      
      // Get competitor data if it exists
      const competitor = item.competitors && item.competitors[internalName];
      
      if (competitor) {
        console.log(`${item.keyword} - Found ${internalName} data:`, 
          JSON.stringify(competitor, null, 2));
        
        // Use current position
        values.push(competitor.current !== undefined && competitor.current !== null
          ? competitor.current.toString()
          : 'Not ranking');
        
        // Use previous position
        values.push(competitor.previous !== undefined && competitor.previous !== null
          ? competitor.previous.toString()
          : 'Not ranking');
        
        // Calculate movement
        const movement = calculateMovement(competitor.previous, competitor.current);
        values.push(movement);
      } else {
        console.log(`${item.keyword} - No data for ${internalName}, using 'Not ranking'`);
        values.push('Not ranking');
        values.push('Not ranking');
        values.push('0');
      }
    }
    
    // Add the row to CSV content
    csvContent += createCSVRow(values);
  });
  
  return csvContent;
};

/**
 * Calculate movement between previous and current positions
 */
function calculateMovement(previous: number | null | undefined, current: number | null | undefined): string {
  if (previous === undefined || previous === null || current === undefined || current === null) {
    return '0';
  }
  
  // If either position is 0 (not ranking), handle appropriately
  if (previous === 0 && current > 0) {
    return '+'; // New ranking
  }
  if (previous > 0 && current === 0) {
    return '-'; // Lost ranking
  }
  if (previous === 0 && current === 0) {
    return '0'; // Still not ranking
  }
  
  // Calculate the difference (positive value means ranking got worse - higher number)
  const diff = previous - current;
  
  if (diff > 0) {
    return `+${diff}`; // Improved ranking
  } else if (diff < 0) {
    return `${diff}`; // Worse ranking (already has minus sign)
  } else {
    return '0'; // No change
  }
} 